import React, { useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';

const GenesisAI = () => {
  const { user } = useContext(AuthContext);
  const widgetContainerRef = useRef(null);
  const scriptLoaded = useRef(false);

  // Load ElevenLabs widget script only once
  useEffect(() => {
    if (scriptLoaded.current) return;
    
    // Check if script already exists
    const existingScript = document.querySelector('script[src*="elevenlabs/convai-widget"]');
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/@elevenlabs/convai-widget-embed';
      script.async = true;
      script.type = 'text/javascript';
      document.body.appendChild(script);
    }
    
    scriptLoaded.current = true;
  }, []);

  // Create widget element after script loads
  useEffect(() => {
    const createWidget = () => {
      if (widgetContainerRef.current && !widgetContainerRef.current.hasChildNodes()) {
        // Check if custom element is defined
        if (customElements.get('elevenlabs-convai')) {
          const widget = document.createElement('elevenlabs-convai');
          widget.setAttribute('agent-id', 'agent_3201kckfz407fxxv2bek695fjb0b');
          widgetContainerRef.current.appendChild(widget);
        } else {
          // Wait for custom element to be defined
          setTimeout(createWidget, 500);
        }
      }
    };

    // Start checking after a delay to let script load
    const timer = setTimeout(createWidget, 1000);
    
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="space-y-6" data-testid="genesis-ai-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 via-blue-500 to-cyan-400 flex items-center justify-center shadow-lg">
          <i className="fas fa-robot text-white text-2xl"></i>
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Genesis AI</h1>
          <p className="text-gray-500 text-sm">Asistente de Inteligencia Artificial del Instituto</p>
        </div>
      </div>

      {/* Main Content */}
      <Card className="min-h-[600px]">
        <CardHeader className="border-b">
          <CardTitle className="flex items-center gap-2">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            Habla con Genesis
          </CardTitle>
          <CardDescription>
            Puedes hacerle preguntas sobre el Instituto, las clases, planes de estudio y más. 
            Genesis puede escucharte y responderte por voz.
          </CardDescription>
        </CardHeader>
        
        <CardContent className="p-6">
          <div className="flex flex-col items-center justify-center space-y-6">
            {/* Instructions */}
            <div className="text-center max-w-md space-y-3">
              <div className="flex items-center justify-center gap-2 text-purple-600">
                <i className="fas fa-microphone text-2xl"></i>
                <i className="fas fa-arrow-right"></i>
                <i className="fas fa-robot text-2xl"></i>
                <i className="fas fa-arrow-right"></i>
                <i className="fas fa-volume-up text-2xl"></i>
              </div>
              <p className="text-gray-600">
                Haz clic en el botón de abajo para iniciar una conversación con Genesis.
                Puedes hablar o escribir tus preguntas.
              </p>
            </div>

            {/* ElevenLabs Widget Container */}
            <div 
              ref={widgetContainerRef}
              className="w-full flex justify-center py-8"
              id="elevenlabs-widget-container"
            >
              {/* Widget will be inserted here dynamically */}
            </div>

            {/* Tips */}
            <div className="bg-blue-50 rounded-lg p-4 max-w-lg">
              <h4 className="font-semibold text-blue-800 mb-2">
                <i className="fas fa-lightbulb mr-2"></i>
                Consejos para mejores respuestas:
              </h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Menciona el nombre de la clase o materia específica</li>
                <li>• Pregunta sobre fechas, horarios o contenido del plan de estudios</li>
                <li>• Puedes preguntar sobre procedimientos del Instituto</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Footer info */}
      <div className="text-center text-sm text-gray-500">
        <p>
          <i className="fas fa-info-circle mr-1"></i>
          Genesis está entrenado con los materiales y planes de estudio del Instituto Bíblico Apostólico
        </p>
      </div>
    </div>
  );
};

export default GenesisAI;
